import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  XCircle,
  CheckCircle2,
  Loader2,
  Mail,
  FileText,
  Clock,
} from 'lucide-react';
import type { Customer, Invoice, PaymentRequest, RequestStatus } from '@ach/shared';
import { formatUsd } from '@ach/shared';

import { api, ApiError } from '../lib/api.js';
import {
  Spinner,
  ErrorNote,
  WarningNote,
  SuccessNote,
  InfoNote,
  StatusBadge,
  ParseBadge,
} from './components.js';
import { BankReveal } from './BankReveal.js';

interface EmailRow {
  kind: string;
  to_email: string;
  subject: string;
  mailgun_id: string | null;
  status: string;
  error: string | null;
  sent_at: string;
}

export interface SubmissionSummary {
  id: string;
  amountCents: number;
  submittedAt: string;
  submittedIp: string | null;
  submittedUserAgent: string | null;
  authorizationAcceptedAt: string;
  authorizationSignatureName: string;
  authorizationVersion: number;
  authorizationBody: string;
  bankAccountId: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  accountLast4: string;
  accountHolderName: string | null;
}

interface Detail {
  request: PaymentRequest & { lockedAt: string | null; recordedNote: string | null };
  customer: Customer | null;
  invoices: (Invoice & { storagePath: string | null })[];
  emails: EmailRow[];
  submission: SubmissionSummary | null;
}

export function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api.get<Detail>(`/api/admin/payment-requests/${id}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load that request.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: 'send' | 'resend' | 'cancel' | 'record', label: string) {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<{ sent?: boolean; error?: string | null }>(
        `/api/admin/payment-requests/${id}/${action}`,
        action === 'record' ? {} : undefined,
      );
      if ((action === 'send' || action === 'resend') && result.sent === false) {
        setError(`The request is live but the email did not go out: ${result.error}`);
      } else {
        setNotice(label);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !detail) return <ErrorNote>{error}</ErrorNote>;
  if (!detail) return <Spinner label="Loading request" />;

  const { request, customer, invoices, emails, submission } = detail;
  const status = request.status as RequestStatus;
  const canSend = status === 'draft';
  const canResend = status === 'sent' || status === 'viewed' || status === 'expired';
  const canCancel = ['draft', 'sent', 'viewed', 'expired'].includes(status);
  const canRecord = status === 'submitted';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin" className="text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-5 w-5" aria-label="Back to dashboard" />
        </Link>
        <h1 className="text-xl font-semibold text-slate-900">
          {customer?.name ?? 'Payment request'}
        </h1>
        <StatusBadge status={status} />
        <span className="ml-auto text-lg font-bold text-slate-900">
          {formatUsd(request.totalAmountCents)}
        </span>
      </div>

      {params.get('emailFailed') === '1' && (
        <WarningNote>
          The request was created and its link is live, but the email did not go out. Use Resend
          below once Mailgun is working — it mints a fresh link.
        </WarningNote>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <SuccessNote>{notice}</SuccessNote>}

      {request.lockedAt && (
        <WarningNote>
          This link was locked after too many failed security answers on{' '}
          {new Date(request.lockedAt).toLocaleString()}. Resend to give the customer a new one.
        </WarningNote>
      )}

      <div className="flex flex-wrap gap-2">
        {canSend && (
          <button
            className="btn-primary"
            onClick={() => void act('send', 'Sent to the customer.')}
            disabled={busy !== null}
          >
            {busy === 'send' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            Send to customer
          </button>
        )}
        {canResend && (
          <button
            className="btn-secondary"
            onClick={() => void act('resend', 'Sent again with a fresh link.')}
            disabled={busy !== null}
          >
            {busy === 'resend' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            Resend
          </button>
        )}
        {canRecord && (
          <button
            className="btn-primary"
            onClick={() => void act('record', 'Marked as scheduled at the bank.')}
            disabled={busy !== null}
          >
            {busy === 'record' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            )}
            Mark as scheduled at the bank
          </button>
        )}
        {canCancel && (
          <button
            className="btn-danger ml-auto"
            onClick={() => void act('cancel', 'Cancelled. The link no longer works.')}
            disabled={busy !== null}
          >
            <XCircle className="h-4 w-4" aria-hidden />
            Cancel request
          </button>
        )}
      </div>

      {submission && <BankReveal submission={submission} customerName={customer?.name ?? ''} />}

      <section className="card overflow-hidden">
        <h2 className="border-b border-slate-100 px-4 py-3 font-semibold text-slate-900">
          Invoices
        </h2>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Number</th>
              <th className="px-4 py-2 font-medium">Date</th>
              <th className="px-4 py-2 font-medium">PO</th>
              <th className="px-4 py-2 font-medium">Source</th>
              <th className="px-4 py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="px-4 py-2.5 font-medium text-slate-800">
                  {invoice.invoiceNumber}
                  {invoice.storagePath && (
                    <InvoiceLink path={invoice.storagePath} name={invoice.originalFilename} />
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{invoice.invoiceDate ?? '—'}</td>
                <td className="px-4 py-2.5 text-slate-600">{invoice.poNumber ?? '—'}</td>
                <td className="px-4 py-2.5">
                  <ParseBadge status={invoice.parseStatus} />
                </td>
                <td className="px-4 py-2.5 text-right font-medium text-slate-900">
                  {formatUsd(invoice.amountCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">
          <Clock className="h-4 w-4 text-slate-400" aria-hidden />
          Timeline
        </h2>
        <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          <Row label="Created" value={request.createdAt} />
          <Row label="Sent" value={request.sentAt} />
          <Row label="First opened by customer" value={request.firstViewedAt} />
          <Row label="Submitted" value={request.submittedAt} />
          <Row label="Recorded" value={request.recordedAt} />
          <Row label="Link expires" value={request.expiresAt} />
          <div className="flex gap-2">
            <dt className="text-slate-500">Security question</dt>
            <dd className="text-slate-800">
              {request.verificationKind === 'zip' ? 'Billing ZIP' : 'Invoice total'}
            </dd>
          </div>
          {request.tokenPrefix && (
            <div className="flex gap-2">
              <dt className="text-slate-500">Link ID</dt>
              <dd className="font-account text-slate-800">{request.tokenPrefix}…</dd>
            </div>
          )}
        </dl>
        {request.recordedNote && (
          <p className="border-t border-slate-100 pt-3 text-sm text-slate-600">
            <span className="text-slate-500">Note: </span>
            {request.recordedNote}
          </p>
        )}
      </section>

      <section className="card overflow-hidden">
        <h2 className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 font-semibold text-slate-900">
          <Mail className="h-4 w-4 text-slate-400" aria-hidden />
          Emails
        </h2>
        {emails.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-500">Nothing sent yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {emails.map((email, index) => (
              <div key={index} className="px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      email.status === 'queued'
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {email.status}
                  </span>
                  <span className="text-slate-800">{email.subject}</span>
                  <span className="text-slate-500">→ {email.to_email}</span>
                  <span className="ml-auto text-slate-400">
                    {new Date(email.sent_at).toLocaleString()}
                  </span>
                </div>
                {email.error && <p className="mt-1 text-xs text-red-700">{email.error}</p>}
                {email.mailgun_id && (
                  <p className="mt-1 font-account text-xs text-slate-400">{email.mailgun_id}</p>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-slate-100 px-4 py-2">
          <p className="text-xs text-slate-500">
            &ldquo;queued&rdquo; means Mailgun accepted it, not that it arrived. Check the Mailgun
            event log with the message ID if a customer says nothing came.
          </p>
        </div>
      </section>

      {!submission && status !== 'draft' && (
        <InfoNote>
          Waiting on the customer. Nothing is debited until you schedule it at the bank, after they
          submit.
        </InfoNote>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value ? new Date(value).toLocaleString() : '—'}</dd>
    </div>
  );
}

/**
 * Opens the invoice PDF.
 *
 * A plain link now. Previously this had to call the API for a fresh signed URL
 * because the one minted at upload had expired; the file streams from an
 * authenticated route instead, so the session cookie is the whole credential
 * and the link never goes stale.
 */
function InvoiceLink({ path, name }: { path: string; name: string | null }) {
  return (
    <a
      href={`/api/admin/invoices/file?path=${encodeURIComponent(path)}`}
      target="_blank"
      rel="noreferrer"
      className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-gold-700 hover:underline"
      title={name ?? 'Open the invoice PDF'}
    >
      <FileText className="h-3 w-3" aria-hidden />
      PDF
    </a>
  );
}
